import { useEffect, useRef, useState } from "react";
import { Music } from "lucide-react";

const TARGET_VOLUME = 0.35;

const fadeVolume = (audio, to, ms = 400) => {
  const from = audio.volume;
  const start = performance.now();
  const step = (t) => {
    const k = Math.min(1, Math.max(0, (t - start) / ms));
    audio.volume = from + (to - from) * k;
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

export const MusicPlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [audible, setAudible] = useState(false);

  // Autoplay strategy:
  // 1) Try unmuted autoplay (works where the browser allows it).
  // 2) Fall back to MUTED autoplay (always allowed) so music truly starts on page open,
  //    then unmute + fade in on the guest's first touch/click anywhere.
  // 3) If even that is blocked (rare), the first gesture starts playback with sound.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0;
    audio.load();

    let cancelled = false;

    const unmuteWithFade = () => {
      audio.muted = false;
      fadeVolume(audio, TARGET_VOLUME, 400);
      setAudible(true);
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("touchstart", onGesture);
      window.removeEventListener("keydown", onGesture);
    };

    const onGesture = () => {
      if (audio.paused) {
        audio.muted = false;
        audio
          .play()
          .then(() => {
            unmuteWithFade();
            setPlaying(true);
          })
          .catch(() => {});
      } else {
        unmuteWithFade();
      }
    };

    const boot = async () => {
      try {
        await audio.play();
        if (cancelled) return;
        unmuteWithFade();
        setPlaying(true);
      } catch {
        try {
          audio.muted = true;
          await audio.play();
          if (cancelled) return;
          setPlaying(true); // playing silently; first gesture unmutes
        } catch {
          // fully blocked; first gesture starts it
        }
      }
      window.addEventListener("pointerdown", onGesture);
      window.addEventListener("touchstart", onGesture, { passive: true });
      window.addEventListener("keydown", onGesture);
    };
    boot();

    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("touchstart", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
  }, []);

  // Pause while the guest is on another tab/app, resume when they come back
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    let resumeOnReturn = false;
    const onVisibility = () => {
      if (document.hidden) {
        resumeOnReturn = !audio.paused;
        if (resumeOnReturn) audio.pause();
      } else if (resumeOnReturn) {
        resumeOnReturn = false;
        audio.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing && audio.muted) {
      // playing silently (autoplay) — a tap means "give me sound"
      audio.muted = false;
      audio.volume = 0;
      fadeVolume(audio, TARGET_VOLUME, 400);
      setAudible(true);
      return;
    }
    if (playing) {
      fadeVolume(audio, 0, 300);
      setTimeout(() => audio.pause(), 320);
      setPlaying(false);
    } else {
      audio.muted = false;
      audio.volume = 0;
      audio.play().then(() => fadeVolume(audio, TARGET_VOLUME, 400)).catch(() => {});
      setPlaying(true);
      setAudible(true);
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        data-testid="background-music-audio"
        src="/background-music.mp3"
        loop
        preload="auto"
      />
      <div
        className="music-fab fixed left-6 min-[1232px]:left-[calc(15vw+1.5rem)] z-50 flex items-center gap-3"
      >
        <button
          type="button"
          data-testid="music-toggle-button"
          aria-label={playing && audible ? "قطع موسیقی" : "پخش موسیقی"}
          onClick={toggle}
          onPointerDown={(e) => e.stopPropagation()}
          className={`relative flex items-center justify-center w-12 h-12 rounded-full border backdrop-blur transition-all duration-300 active:scale-95 ${
            audible
              ? "border-[#C29B38] bg-white/80 text-[#C29B38] shadow-[0_10px_30px_-12px_rgba(194,155,56,0.5)]"
              : "pulse-ring border-[#C29B38] bg-white/80 text-[#C29B38]"
          }`}
        >
          {playing ? (
            <span className="music-eq flex items-end gap-[3px] h-4" aria-hidden="true">
              <span style={{ animationDelay: "0s" }} />
              <span style={{ animationDelay: "0.25s" }} />
              <span style={{ animationDelay: "0.5s" }} />
            </span>
          ) : (
            <Music size={17} />
          )}
        </button>
      </div>
    </>
  );
};
